import {
  LootBoxInfo,
  getLootBoxSpriteInfo,
  TILE_SIZE,
} from "../models/common";
import { LootBox } from "../models/loot-box";
import { WindowManager } from "../ui/window-manager";
import { getLootBagSprite } from "../utils/sprite-utils";

/**
 * Registers listeners for loot-bag syncing.
 * Works for both MainScene and MarketScene.
 * Handles: loot-bag-update, add-loot-bag, update-loot-bag-sprite events
 */
export function registerItemListeners(
  scene: any,
  windowManager?: WindowManager,
) {
  if (!window.socket) return;

  // Batch loot-bag update on initial load
  window.socket.on("loot-bag-update", (lootBoxes: LootBoxInfo[]) => {
    if (!scene.mapHandler) return;
    if (!scene.mapHandler.lootBoxes) scene.mapHandler.lootBoxes = [];

    lootBoxes.forEach((lbData: LootBoxInfo) => {
      const exists: LootBox | undefined = scene.mapHandler.lootBoxes.find(
        (lb: LootBox) => lb.info.x === lbData.x && lb.info.y === lbData.y,
      );

      if (!exists) {
        // Create new loot box
        const py = lbData.y * TILE_SIZE + TILE_SIZE / 2;
        const lootBagSpriteInfo = getLootBoxSpriteInfo(lbData);
        const lootBagSprite = getLootBagSprite(scene, lootBagSpriteInfo);

        // Set depth based on Y position for proper rendering order
        lootBagSprite.setDepth(1000 + py);

        // Make UI camera ignore loot bag sprite
        if (scene.uiCamera) {
          scene.uiCamera.ignore(lootBagSprite);
        }

        const lootBox = new LootBox(lbData, lootBagSprite);
        lootBox.createBlueprintBackground(scene);
        scene.mapHandler.lootBoxes.push(lootBox);
      } else {
        // Update existing loot box
        exists.info = lbData;
        const lootBagSpriteInfo = getLootBoxSpriteInfo(lbData);

        // Recreate sprite if items changed
        if (!exists.fixed && exists.sprite) {
          const py = exists.sprite.y;
          exists.sprite.destroy();
          exists.sprite = getLootBagSprite(scene, lootBagSpriteInfo);
          exists.sprite.setDepth(1000 + py);

          if (scene.uiCamera) {
            scene.uiCamera.ignore(exists.sprite);
          }
        }
        exists.createBlueprintBackground(scene);
      }
    });
  });

  // Add single loot bag dynamically
  window.socket.on("add-loot-bag", (data: LootBoxInfo) => {
    if (!scene.mapHandler) return;
    if (!scene.mapHandler.lootBoxes) scene.mapHandler.lootBoxes = [];

    const existing = scene.mapHandler.lootBoxes.find(
      (lb: LootBox) => lb.info.x === data.x && lb.info.y === data.y,
    );

    const lootBagSpriteInfo = getLootBoxSpriteInfo(data);

    if (existing) {
      // Update existing loot box info and sprite if items changed
      existing.info = data;
      if (existing.sprite) {
        const py = existing.sprite.y;
        existing.sprite.destroy();
        existing.sprite = getLootBagSprite(scene, lootBagSpriteInfo);
        existing.sprite.setDepth(1000 + py);

        if (scene.uiCamera) {
          scene.uiCamera.ignore(existing.sprite);
        }
      }
      existing.createBlueprintBackground(scene);
      return;
    }

    // Create new loot box
    const py = data.y * TILE_SIZE + TILE_SIZE / 2;
    const lootBagSprite = getLootBagSprite(scene, lootBagSpriteInfo);
    lootBagSprite.setDepth(1000 + py);

    if (scene.uiCamera) {
      scene.uiCamera.ignore(lootBagSprite);
    }

    const lootBox = new LootBox(data, lootBagSprite);
    lootBox.createBlueprintBackground(scene);
    scene.mapHandler.lootBoxes.push(lootBox);
  });

  // Update loot bag sprite when items change
  window.socket.on(
    "update-loot-bag-sprite",
    (data: { x: number; y: number; items: any[] }) => {
      if (!scene.mapHandler || !scene.mapHandler.lootBoxes) return;

      const lootBox = scene.mapHandler.lootBoxes.find(
        (lb: LootBox) => lb.info.x === data.x && lb.info.y === data.y,
      );

      if (lootBox) {
        lootBox.info.items = data.items || [];

        if (lootBox.sprite) {
          const py = lootBox.sprite.y;
          lootBox.sprite.destroy();

          const lootBagSpriteInfo = getLootBoxSpriteInfo(lootBox.info);
          lootBox.sprite = getLootBagSprite(scene, lootBagSpriteInfo);
          lootBox.sprite.setDepth(1000 + py);

          if (scene.uiCamera) {
            scene.uiCamera.ignore(lootBox.sprite);
          }
        }

        lootBox.createBlueprintBackground(scene);
      }
    },
  );
}

/**
 * Unregisters item listeners.
 */
export function unregisterItemListeners() {
  if (!window.socket) return;
  window.socket.off("loot-bag-update");
  window.socket.off("add-loot-bag");
  window.socket.off("update-loot-bag-sprite");
}
